import { inject,Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import {
    DesignerBranch,
    DesignerModuleName,
    DesignerSelectorRule,
    GraphDotnetService, graphql,
    MediaclipMutationAddStoreDesignerBranchRuleArgs,
    MediaclipMutationDeleteStoreDesignerBranchRuleArgs,
    MediaclipMutationUpdateStoreDefaultDesignerBranchArgs,
    MediaclipMutationUpdateStoreDesignerBranchRuleArgs
} from 'app/shared/graph';

@Injectable({ providedIn: 'root' })
export class DesignerReleaseChannelService {
    private readonly graphDotnetService = inject(GraphDotnetService);

    public getDesignerReleaseChannels(): Observable<DesignerBranch[]> {
        return this.graphDotnetService.query(graphql`
                query designerReleaseChannels {
                    designerReleaseChannels {
                        id
                        version
                    }
                }
            `,
            {}
        ).pipe(map((q) => q.designerReleaseChannels));
    }

    public getStoreAllowedDesignerReleaseChannels(storeId: string): Observable<DesignerBranch[]> {
        return this.graphDotnetService.query(graphql`
                query getStoreAllowedDesignerReleaseChannels($storeId: String!) {
                    store(id: $storeId) {
                      allowedDesignerReleaseChannels {
                          id
                          version
                      }
                    }
                }
            `,
            {
                storeId: storeId
            }
        ).pipe(map((q) => q.store.allowedDesignerReleaseChannels));
    }

    public getDesignerReleaseChannelConfiguration(storeId: string): Observable<{ designerBranch: DesignerBranch, rules?: Array<DesignerSelectorRule> }> {
        return this.graphDotnetService.query(graphql`
                query getStoreDesignerBranch($storeId: String!) {
                    store(id: $storeId) {
                        designerReleaseChannel {
                           id
                           version
                        }
                        designerBranchRules {
                            id
                            label
                            weight
                            designerBranch
                            module
                        }
                    }
                }
            `,
            {
                storeId: storeId
            }
        ).pipe(map((q) => ({
            designerBranch: q.store!.designerReleaseChannel!,
            rules: q.store!.designerBranchRules
        })));
    }

    public updateStoreDefaultDesignerReleaseChannel(storeId: string, designerBranch: string): Observable<void> {
        return this.graphDotnetService.mutate<MediaclipMutationUpdateStoreDefaultDesignerBranchArgs>(
            graphql`
                mutation updateStoreDefaultDesignerBranch($input: UpdateStoreDefaultDesignerBranchInput!) {
                    updateStoreDefaultDesignerBranch(input: $input)
                }
            `,
            {
                input: {
                    storeId: storeId,
                    designerBranch: designerBranch
                }
            }
        ).pipe(
            map(m => m.updateStoreDefaultDesignerBranch!)
        );
    }

    public addStoreDesignerReleaseChannelRule(
        storeId: string,
        designerBranch: string,
        weight: number,
        label: string,
        module?: DesignerModuleName | undefined | null
    ): Observable<DesignerSelectorRule> {
        return this.graphDotnetService.mutate<MediaclipMutationAddStoreDesignerBranchRuleArgs>(
            graphql`
                mutation addStoreDesignerBranchRule($input: AddDesignerBranchRuleInput!) {
                    addStoreDesignerBranchRule(input: $input) {
                        id
                    }
                }
            `,
            {
                input: {
                    storeId: storeId,
                    designerBranch: designerBranch,
                    weight: weight,
                    label: label,
                    module: module
                }
            }
        ).pipe(
            map(m => m.addStoreDesignerBranchRule!)
        );
    }

    public deleteStoreDesignerReleaseChannelRule(storeId: string, ruleId: string): Observable<void> {
        return this.graphDotnetService.mutate<MediaclipMutationDeleteStoreDesignerBranchRuleArgs>(
            graphql`
                mutation deleteStoreDesignerBranchRule($input: DeleteDesignerBranchRuleInput!) {
                    deleteStoreDesignerBranchRule(input: $input)
                }
            `,
            {
                input: {
                    storeId: storeId,
                    ruleId: ruleId
                }
            }
        ).pipe(
            map(m => m.updateStoreDesignerBranchRule!)
        );
    }

    public updateStoreDesignerReleaseChannelRule(
        storeId: string,
        ruleId: string,
        designerBranch: string,
        weight: number,
        label: string,
        module?: DesignerModuleName | undefined | null
    ): Observable<void> {
        return this.graphDotnetService.mutate<MediaclipMutationUpdateStoreDesignerBranchRuleArgs>(
            graphql`
                mutation updateStoreDesignerBranchRule($input: UpdateDesignerBranchRuleInput!) {
                    updateStoreDesignerBranchRule(input: $input)
                }
            `,
            {
                input: {
                    storeId: storeId,
                    ruleId: ruleId,
                    designerBranch: designerBranch,
                    weight: weight,
                    label: label,
                    module: module
                }
            }
        ).pipe(
            map(m => m.updateStoreDesignerBranchRule!)
        );
    }
}
